// The lay looks of the World fields `seaMineLay` and `deployableLay`
// (v2-the-deep.md §11.8, mockup rounds 071 and 072, R2538, R2652).
//
// A lay look is what a Sapper draws while it lays a sea mine or a
// deployable: the object leaving the stow, its arm or its anchor, and the
// loud lay of §11.1. Each look ports its card of
// `archive/mockups/071-sapper-lay-mines.html` or
// `072-sapper-lay-deployables.html` point for point.
//
// Every sea mine lay look adds two cards: card 18 Loud Clock (an arc round
// the own Sapper drains over the loud lay; own information, so a hostile
// view draws none) and card 20 Bubble Train (the lay frees five bubbles,
// and an unarmed sea mine lets one bubble go each 0.35 s, until a white ring
// pops at the arm). Every deployable lay look adds card 20 Loud Lay (a
// dashed ring round the Sapper drains over the loud lay and sound rings
// leave the hull; a hostile view draws a bearing from its listener to the
// loud Sapper, and a HEARD frame on the deployable when it is live).
//
// These are the marks alone. The layer that draws sea objects on the map
// owns the timing and the state: it fills `LayScene` and `DeployLay` from
// the sea objects of the State frame and from its own clock. The round
// pages drew the deployable with a placeholder mark, the same in rounds 070
// to 074; this module keeps that placeholder (`paintDeployPlaceholder`), so
// each look draws whole, and R2652 says how the client lane puts the
// deployable mark of the `deployable` field in its place.
//
// This is a leaf (`leaves.test.ts`): it paints into a context it is handed.

import { DEPLOYABLE_LAY_STYLES, SEA_MINE_LAY_STYLES } from '../gen/balance';
import { seamarks as WORDS } from '../gen/strings';
import {
  Fl, S, SEA_INK, arc, circle, disc, inOut, lerp, line, lw, mulberry32, outCubic, px, smooth, soft,
  text, type Ctx, type Pt,
} from './seaobject-kit';
import { TAU, clamp01 } from './math';
import { rgba } from './color';
import type { DeployableKind } from './deployable-marks';
import type { SeaMineKind } from './seamine-marks';

/** One look of the `seaMineLay` field. */
export type SeaMineLayStyle = (typeof SEA_MINE_LAY_STYLES)[number];
/** One look of the `deployableLay` field. */
export type DeployableLayStyle = (typeof DEPLOYABLE_LAY_STYLES)[number];

const WHITE = SEA_INK.white, PALE = SEA_INK.pale, DEEP = SEA_INK.deep;

function outBack(u: number): number {
  const v = clamp01(u) - 1;
  return 1 + 2.70158 * v * v * v + 1.70158 * v * v;
}
/** A damped spring from 0 to 1 that passes 1 by about 18 %. */
export function spring(u: number): number { return u <= 0 ? 0 : 1 - Math.exp(-6 * u) * Math.cos(11 * u); }

// =================================================================================
// Round 071: a Sapper lays a sea mine
// =================================================================================

/** The state of a laid sea mine: it waits for its arm, it is armed, or it
 *  lies inert in protected water (§11.2 rule 5). */
export type LaidState = 'unarmed' | 'armed' | 'inert';

/** One laid sea mine, as a lay look reads it. Every time counts the
 *  seconds of the look, which the layer maps from the map clock. */
export interface LaidMine {
  /** The place of the sea mine in the run of lays, 0 first. */
  i: number;
  /** Where it is now, where it was laid, and where the water it was laid
   *  in is now (the drift of the current at the full speed of the water). */
  x: number; y: number; lx: number; ly: number; wx: number; wy: number;
  /** Its own drift, px a second of the look. */
  dvx: number; dvy: number;
  /** The radius of its disc, and the contact radius of its kind (50 px ×
   *  the noise for a Siren, the base of its hearing). */
  r: number; reach: number;
  col: number;
  /** The seconds since the lay. */
  k: number;
  /** The distance to its layer. */
  d: number;
  state: LaidState;
  /** The share of the arm window run, 0 to 1, as the server states it
   *  (`arm_left_map_ms`, R2737); -1 while the layer is near and no window
   *  runs, and 1 once armed. */
  armU: number;
  /** The seconds since the arm, since the server opened the arm window
   *  (its layer is clear, R2737), and since it entered protected water;
   *  negative before. */
  kArm: number; kFar: number; kIn: number;
  /** True for a sea mine that armed and then disarmed in protected water. */
  dis: boolean;
  /** The seed of its bubbles. */
  seed: number;
  /** The kind, where the caller knows it: the mark of the `seaMine` field
   *  then stands in place of the placeholder (R2657). */
  kind?: SeaMineKind;
}

/** The own Sapper or the contact of a hostile one, at the lay. */
export interface LayHull { x: number; y: number; hdg: number; s: number }

/** Everything a sea mine lay look reads. */
export interface LayScene {
  /** True in the own view. */
  own: boolean;
  /** The ink of the layer's side. */
  col: number;
  H: LayHull;
  mines: readonly LaidMine[];
  /** The seconds since the last lay, and the loud lay: on, its share run
   *  (0 to 1) and its fade (0 to 1). */
  kLast: number; loud: boolean; loudU: number; loudA: number;
  /** The index of the last lay, -1 before the first. */
  lastI: number;
  z: number;
  /** The arm distance of §11.2 rule 2 (seaobjects.toml `arm_clear_px`). */
  armPx: number;
  /** The noise share of the loud lay (seaobjects.toml `lay_noise_mul`). */
  loudMul: number;
  /** The unit heading of the current, and the speed of the water, px a
   *  second of the look. */
  cur: Pt; curV: number;
  /** The font family a figure is written in (the mono stack). */
  face: string;
  /** The mark of the `seaMine` field, which stands in place of the
   *  placeholder disc at `x, y`, at alpha `a` and scale `s` (R2657). */
  mark?: (c: Ctx, m: LaidMine, x: number, y: number, a: number, s: number) => void;
  /** The whole turns an unarmed sea mine spins through its arm window
   *  (presentation.toml `arm_spin_turns`, R2737); 0 or absent spins none. */
  armTurns?: number;
}

/** One sea mine lay look: the hooks its card fills. */
export interface SeaMineLayLook {
  /** The seconds the lay effect draws. */
  layLife?: number;
  /** Draws the lay under the sea mine, not over it. */
  layUnder?: boolean;
  /** Draws the loud tell under the hull. */
  loudUnder?: boolean;
  /** The lay effect of one sea mine. */
  lay?(c: Ctx, m: LaidMine, o: LayScene): void;
  /** The sea mine itself, in its state. Without it the placeholder draws. */
  mine?(c: Ctx, m: LaidMine, o: LayScene): void;
  /** A part on the hull, in hull space. */
  hull?(c: Ctx, o: LayScene): void;
  /** The share the wake grows by while loud. */
  wake?(o: LayScene): number;
  /** The loud tell round the hull. */
  loud?(c: Ctx, H: LayHull, o: LayScene): void;
  /** A part under everything, in world space. */
  under?(c: Ctx, o: LayScene): void;
}

/** The four corners of a box, clockwise from the top left: the signs of x
 *  and of y. */
const CORNER_X = [-1, 1, 1, -1] as const, CORNER_Y = [-1, -1, 1, 1] as const;

/** The path of `P` through three points, with no array of points. */
function path3(c: Ctx, x0: number, y0: number, x1: number, y1: number, x2: number, y2: number, close = true): void {
  c.beginPath(); c.moveTo(x0, y0); c.lineTo(x1, y1); c.lineTo(x2, y2);
  if (close) c.closePath();
}

/** The closed path of `P` through four points, with no array of points. */
function path4(c: Ctx, x0: number, y0: number, x1: number, y1: number, x2: number, y2: number, x3: number, y3: number): void {
  c.beginPath(); c.moveTo(x0, y0); c.lineTo(x1, y1); c.lineTo(x2, y2); c.lineTo(x3, y3); c.closePath();
}

/** The x of the stow and of the stern of the hull; both stand at `H.y`. */
const stowX = (o: LayScene): number => o.H.x - 2 * o.H.s;
const sternX = (o: LayScene): number => o.H.x - 6 * o.H.s;

/** 0 while unarmed, 1 when armed (over `ramp` s), back to 0 over 0.4 s at a
 *  disarm. */
export function armLevel(m: LaidMine, ramp: number): number {
  if (m.state === 'armed') return ramp ? clamp01(m.kArm / ramp) : 1;
  if (m.dis) return clamp01(1 - m.kIn / 0.4);
  return 0;
}

/** The share of the arm window a look draws as progress, 0 to 1: 0 while
 *  the layer is near, the server's share through the window, 1 once armed
 *  (R2737). Every look that draws an arm progress reads it. */
export function armProgress(m: LaidMine): number {
  if (m.state === 'armed') return 1;
  return m.state === 'unarmed' && m.armU > 0 ? m.armU : 0;
}

/** The turn of an unarmed sea mine through its arm window, radians (R2737):
 *  `armTurns` whole turns, eased in and out, so it ends at the flip with
 *  the mark upright. 0 before the window and after the arm. */
export function armSpin(m: LaidMine, o: LayScene): number {
  const turns = o.armTurns ?? 0;
  if (!(turns > 0) || m.state !== 'unarmed' || !(m.armU > 0)) return 0;
  return TAU * turns * inOut(m.armU);
}

/** The body of a laid sea mine: the mark of the `seaMine` field where the
 *  scene names it (R2657), else the placeholder disc of the round. It spins
 *  through the arm window (`armSpin`). */
export function mineBody(c: Ctx, m: LaidMine, o: LayScene, x = m.x, y = m.y, a = 1, s = 1): void {
  spun(c, m, o, x, y, () => {
    if (o.mark) o.mark(c, m, x, y, a, s);
    else disc(c, x, y, m.r * s, m.col, a);
  });
}

/** Runs `draw` turned by `armSpin` round `x, y`: a look that draws its own
 *  body of the sea mine spins it through the arm window too. */
export function spun(c: Ctx, m: LaidMine, o: LayScene, x: number, y: number, draw: () => void): void {
  const spin = armSpin(m, o);
  if (spin === 0) { draw(); return; }
  c.save(); c.translate(x, y); c.rotate(spin); c.translate(-x, -y);
  draw();
  c.restore();
}

/** The placeholder of the round: the disc while unarmed, the disc and its
 *  ring when armed. The `seaMine` mark stands in its place on the map. */
export function defaultLaidMine(c: Ctx, m: LaidMine, o: LayScene): void {
  mineBody(c, m, o);
  const a = armLevel(m, 0);
  if (a > 0) circle(c, m.x, m.y, 5, m.col, 0.9 * a, lw(1, o.z));
}

/** Two noise arcs aft, or on both beams. */
function arcsAft(c: Ctx, H: LayHull, o: LayScene, beams: boolean): void {
  if (!(o.loudA > 0)) return;
  const dirs = beams ? [Math.PI / 2, -Math.PI / 2] : [Math.PI];
  for (let j = 0; j < 2; j++) {
    const q = (o.kLast * 2.4 + j * 0.5) % 1;
    for (const b of dirs) arc(c, H.x, H.y, 9 + 13 * q, b - 0.5, b + 0.5, PALE, 0.75 * o.loudA * (1 - q), lw(1.2, o.z));
  }
}
const wakeX2 = (o: LayScene): number => 1 + 1.2 * o.loudA;

/** The cradle of card 04: the length of the arm and its angle in hull space
 *  at `k` s after the lay. */
function cradle(k: number): { len: number; th: number } {
  const len = k < 0.12 ? (9 * k) / 0.12 : k < 0.55 ? 9 : 9 * Math.max(0, 1 - (k - 0.55) / 0.25);
  const th = k < 0.12 ? Math.PI / 2 : k < 0.42 ? lerp(Math.PI / 2, Math.PI, inOut((k - 0.12) / 0.3)) : Math.PI;
  return { len, th };
}

/** Card 01 Plain Drop: the floor of the round. The sea mine appears, and
 *  its ring appears at the arm, with no effect. */
const plainDrop: SeaMineLayLook = {};

/** Card 02 Stern Slide: the sea mine slides out of the stow and drops off
 *  the stern; at the arm the ring draws round it. The wake doubles. */
const sternSlide: SeaMineLayLook = {
  layLife: 0.9, wake: wakeX2,
  lay(c, m, o) {
    if (m.k < 0.4) {
      const sx = stowX(o), u = inOut(m.k / 0.4);
      mineBody(c, m, o, lerp(sx, m.x, u), lerp(o.H.y, m.y, u));
    } else {
      const u = (m.k - 0.4) / 0.5;
      circle(c, m.x, m.y, 3 + 6 * outCubic(u), WHITE, 0.8 * (1 - u), lw(1, o.z));
    }
  },
  mine(c, m, o) {
    mineBody(c, m, o);
    const a = armLevel(m, 0.35), a0 = -Math.PI / 2;
    if (a <= 0) return;
    if (m.state === 'armed') {
      arc(c, m.x, m.y, 5, a0, a0 + TAU * a, m.col, 0.9, lw(1, o.z));
      if (a < 1) disc(c, m.x + 5 * Math.cos(a0 + TAU * a), m.y + 5 * Math.sin(a0 + TAU * a), 1.1, WHITE, 1);
    } else circle(c, m.x, m.y, 5, m.col, 0.9 * a, lw(1, o.z));
  },
};

/** Card 03: the three rays of the spray fan, off the stern. */
const SPRAY_FAN = [-0.6, 0, 0.6] as const;

/** Card 03 Chute Flash: a chute on the stern flashes, and the sea mine kicks
 *  out astern with a fan of spray. Two noise arcs run aft while loud. */
const chuteFlash: SeaMineLayLook = {
  layLife: 0.5,
  lay(c, m, o) {
    const k = m.k, s0x = sternX(o), s0y = o.H.y;
    if (k < 0.14) {
      const a = 1 - k / 0.14;
      line(c, s0x - 0.5, s0y, s0x - 6, s0y, WHITE, a, lw(1.8, o.z)); disc(c, s0x - 1.5, s0y, 2.6, WHITE, 0.7 * a);
    }
    if (k < 0.22) { const u = outCubic(k / 0.22); mineBody(c, m, o, lerp(s0x - 1, m.x, u), lerp(s0y, m.y, u)); }
    if (k > 0.1) {
      const q = clamp01((k - 0.1) / 0.4);
      for (const b of SPRAY_FAN) {
        const th = Math.PI + b, r0 = 2 + 3 * q, r1 = r0 + 4 * (1 - 0.5 * q);
        line(c, m.x + Math.cos(th) * r0, m.y + Math.sin(th) * r0, m.x + Math.cos(th) * r1, m.y + Math.sin(th) * r1, WHITE, 0.85 * (1 - q), lw(1, o.z));
      }
    }
  },
  mine(c, m, o) {
    defaultLaidMine(c, m, o);
    if (m.state === 'armed' && m.kArm < 0.4) { const q = m.kArm / 0.4; circle(c, m.x, m.y, 5 + 5 * outCubic(q), WHITE, 1 - q, lw(1.3, o.z)); }
  },
  loud(c, H, o) { arcsAft(c, H, o, false); },
};

/** Card 04 Swing Cradle: a cradle arm swings the sea mine round over the
 *  stern; the ring snaps in at the arm. The contact ring turns dashed while
 *  loud. */
const swingCradle: SeaMineLayLook = {
  layLife: 0.8,
  lay(c, m, o) {
    const k = m.k, g = cradle(k), sx = stowX(o), H = o.H, sy = H.y;
    if (k < 0.55) mineBody(c, m, o, sx + Math.cos(g.th) * g.len * H.s, sy + Math.sin(g.th) * g.len * H.s);
    else if (k < 0.7) {
      const u = outCubic((k - 0.55) / 0.15), x0 = sx - 9 * H.s;
      mineBody(c, m, o, lerp(x0, m.x, u), lerp(sy, m.y, u));
    }
    if (k >= 0.6) { const q = (k - 0.6) / 0.2; circle(c, m.x, m.y, 3 + 4 * q, WHITE, 0.75 * (1 - q), lw(1, o.z)); }
  },
  hull(c, o) {
    if (!(o.kLast < 0.8)) return;
    const g = cradle(o.kLast);
    if (g.len < 0.3) return;
    line(c, -2, 0, -2 + Math.cos(g.th) * g.len, Math.sin(g.th) * g.len, WHITE, 1, lw(1, o.z));
    disc(c, -2, 0, 1, WHITE, 1);
  },
  mine(c, m, o) {
    mineBody(c, m, o);
    const a = armLevel(m, 0);
    if (a > 0) {
      const r = m.state === 'armed' && m.kArm < 0.3 ? 8 - 3 * outBack(m.kArm / 0.3) : 5;
      circle(c, m.x, m.y, r, m.col, 0.9 * a, lw(1.1, o.z));
    }
  },
  loud(c, H, o) {
    if (!(o.loudA > 0)) return;
    c.save(); c.lineDashOffset = (-o.kLast * 14) / o.z;
    circle(c, H.x, H.y, 19, o.col, 0.85 * o.loudA, lw(1.3, o.z), [px(4, o.z), px(3, o.z)]);
    c.restore();
  },
};

/** Card 05 Seed Line: a dashed line in the wake; a dash rounds into each
 *  sea mine, and the stretch fades at the arm. A ×1.5 tag rides over the
 *  hull while loud. */
const seedLine: SeaMineLayLook = {
  layLife: 0.4,
  under(c, o) {
    if (!o.own) return;
    const ms = o.mines, sx = sternX(o), sy = o.H.y;
    for (let j = 0; j < ms.length; j++) {
      const a = ms[j], b = ms[j + 1], ex = b ? b.x : sx, ey = b ? b.y : sy;
      const ua = 1 - armLevel(a, 0.4), ub = b ? 1 - armLevel(b, 0.4) : ua, al = 0.6 * Math.max(ua, ub);
      if (al > 0.01) line(c, a.x, a.y, ex, ey, a.col, al, lw(1, o.z), [px(3, o.z), px(3, o.z)]);
    }
  },
  lay(c, m) {
    const u = outCubic(m.k / 0.4), hl = 3 * (1 - u) + 0.01;
    line(c, m.x - hl, m.y, m.x + hl, m.y, m.col, 1, lerp(1.2, 2 * m.r, u));
  },
  mine(c, m, o) {
    mineBody(c, m, o);
    const a = armLevel(m, 0.25);
    if (a < 1) circle(c, m.x, m.y, 5, m.col, 0.8 * (1 - a), lw(1.1, o.z), [2.4, 2.8]);
    if (a > 0) circle(c, m.x, m.y, 5, m.state === 'armed' && m.kArm < 0.25 ? WHITE : m.col, 0.9 * a, lw(1.1, o.z));
  },
  loud(c, H, o) {
    text(c, WORDS.mul({ mul: o.loudMul }), H.x + 2, H.y - 26, `600 ${9 / o.z}px ${o.face}`, PALE, 0.95 * o.loudA);
  },
};

/** Card 07 Ripple: three ripples spread from the drop; at the arm one
 *  ripple closes onto the sea mine and becomes its ring. The bloom under
 *  the hull swells while loud. */
const ripple: SeaMineLayLook = {
  layLife: 1.2, loudUnder: true,
  lay(c, m, o) {
    for (let j = 0; j < 3; j++) {
      const q = (m.k - j * 0.16) / 0.85;
      if (q < 0 || q > 1) continue;
      circle(c, m.wx, m.wy, 3 + 14 * outCubic(q), j ? PALE : WHITE, 0.75 * (1 - q), lw(1, o.z));
    }
  },
  mine(c, m, o) {
    mineBody(c, m, o);
    if (m.state === 'armed' && m.kArm < 0.35) {
      const q = m.kArm / 0.35;
      circle(c, m.x, m.y, 15 - 10 * outCubic(q), WHITE, 0.3 + 0.65 * q, lw(1.1, o.z));
    } else {
      const a = armLevel(m, 0);
      if (a > 0) circle(c, m.x, m.y, 5, m.col, 0.9 * a, lw(1, o.z));
    }
  },
  loud(c, H, o) {
    if (!(o.loudA > 0)) return;
    if (o.own) { c.save(); c.translate(H.x, H.y); c.scale(1, 0.5); soft(c, 0, 0, 30, o.col, 0.42 * o.loudA); c.restore(); }
    else soft(c, H.x, H.y, 24, o.col, 0.3 * o.loudA);
  },
};

/** Card 08 Countdown Ring: the ring of an unarmed sea mine grows as an arc
 *  over the arm window the server states, closed at the arm (R2737). A sea
 *  mine whose layer is near draws no arc. The contact ring of the hull
 *  brightens while loud. */
const countdownRing: SeaMineLayLook = {
  mine(c, m, o) {
    mineBody(c, m, o);
    const a = armLevel(m, 0);
    if (a < 1) circle(c, m.x, m.y, 5, PALE, 0.2, lw(1, o.z));
    const u = m.state === 'unarmed' ? armProgress(m) : 0;
    if (u > 0) arc(c, m.x, m.y, 5, -Math.PI / 2, -Math.PI / 2 + TAU * u, m.col, 0.9, lw(1.2, o.z));
    if (a > 0) circle(c, m.x, m.y, 5, m.col, 0.9 * a, lw(1.2, o.z));
    if (m.state === 'armed' && m.kArm < 0.35) {
      const q = m.kArm / 0.35;
      circle(c, m.x, m.y, 5, WHITE, 1 - q, lw(1.5, o.z)); line(c, m.x, m.y - 6, m.x, m.y - 9.5, WHITE, 1 - q, lw(1.2, o.z));
    }
  },
  loud(c, H, o) { circle(c, H.x, H.y, 19, o.col, 0.8 * o.loudA, lw(1.1 + 0.8 * o.loudA, o.z)); },
};

/** Card 10 Safe Bubble: a dashed circle of the arm distance rides with the
 *  Sapper while one of its sea mines waits for its arm window, and fades
 *  over the window the server states (R2737); a sea mine is hollow until
 *  the arm. The contact ring pulses while loud. */
const safeBubble: SeaMineLayLook = {
  under(c, o) {
    if (!o.own) return;
    let A = 0;
    for (const m of o.mines) {
      if (m.state !== 'unarmed') continue;
      if (m.armU < 0) A = Math.max(A, clamp01(m.k / 0.2));
      else A = Math.max(A, 1 - armProgress(m));
    }
    if (!(A > 0)) return;
    disc(c, o.H.x, o.H.y, o.armPx, o.col, 0.04 * A);
    circle(c, o.H.x, o.H.y, o.armPx, o.col, 0.5 * A, lw(1, o.z), [px(4, o.z), px(4, o.z)]);
  },
  mine(c, m, o) {
    const a = armLevel(m, 0.2);
    if (o.mark) {
      // The mark of the field states the arm itself (R2657).
      mineBody(c, m, o);
      if (a > 0) circle(c, m.x, m.y, 5, m.col, 0.9 * a, lw(1, o.z));
    } else {
      // The hollow body spins through the arm window; its dot sits off the
      // centre, so the turn shows (R2737).
      if (a < 1) {
        spun(c, m, o, m.x, m.y, () => {
          circle(c, m.x, m.y, m.r - 0.4, m.col, 0.95 * (1 - a), lw(1.2, o.z));
          disc(c, m.x, m.y + (m.state === 'unarmed' && m.armU > 0 ? -0.9 : 0), 0.9, m.col, 1 - a);
        });
      }
      if (a > 0) { disc(c, m.x, m.y, m.r * a, m.col, 1); circle(c, m.x, m.y, 5, m.col, 0.9 * a, lw(1, o.z)); }
    }
    if (m.state === 'armed' && m.kArm < 0.35) {
      const q = m.kArm / 0.35;
      circle(c, m.x, m.y, 5 + 4 * outCubic(q), WHITE, 0.9 * (1 - q), lw(1.1, o.z));
    }
  },
  loud(c, H, o) {
    circle(c, H.x, H.y, 19, o.col, o.loudA * (0.2 + 0.65 * Math.abs(Math.sin(o.kLast * Math.PI * 3))), lw(1.5, o.z));
  },
};

/** Card 18 Loud Clock, which joins every sea mine lay look: an arc at 23 px
 *  round the own Sapper drains over the loud lay, with a tick at the top.
 *  It is own information, so a hostile view draws none. */
export function paintLoudClock(c: Ctx, H: LayHull, o: LayScene): void {
  if (!o.own || !o.loud) return;
  const a = smooth(0, 0.08, o.kLast), R = 23;
  circle(c, H.x, H.y, R, o.col, 0.16 * a, lw(1.6, o.z));
  arc(c, H.x, H.y, R, -Math.PI / 2, -Math.PI / 2 + TAU * (1 - o.loudU), o.col, 0.9 * a, lw(1.6, o.z));
  line(c, H.x, H.y - R + 2.5, H.x, H.y - R - 2.5, WHITE, o.kLast < 0.25 ? 1 : 0.6, lw(1.3, o.z));
}

/** Card 20 Bubble Train at the lay, which joins every sea mine lay look:
 *  the lay frees five bubbles that drift off with the current. It draws
 *  for 1.6 s. */
export function paintLayBubbles(c: Ctx, m: LaidMine, o: LayScene): void {
  const rnd = mulberry32(m.seed + 20);
  for (let j = 0; j < 5; j++) {
    const q = m.k - j * 0.07, b = rnd() * TAU, sp = 3 + 3 * rnd();
    if (q < 0 || q > 1.3) continue;
    const x = m.lx + (o.cur[0] * (o.curV + 6) + Math.cos(b) * sp) * q, y = m.ly + (o.cur[1] * (o.curV + 6) + Math.sin(b) * sp) * q;
    circle(c, x, y, 0.7 + (0.9 * q) / 1.3, WHITE, 0.85 * (1 - q / 1.3), lw(0.9, o.z));
  }
}

/** Card 20 Bubble Train on the sea mine: an unarmed sea mine lets one
 *  bubble go each 0.35 s, and at the arm a white ring pops and the stream
 *  stops. */
export function paintMineBubbles(c: Ctx, m: LaidMine, o: LayScene): void {
  const armedFor = m.state === 'armed' || m.dis ? m.kArm : -1;
  for (let j = 0; j < 4; j++) {
    const age = (m.k % 0.35) + j * 0.35, birth = m.k - age;
    if (birth < 0.3 || age > 1.3) continue;
    if (armedFor >= 0 && birth > m.k - armedFor) continue;
    const x = m.x - m.dvx * age + o.cur[0] * (o.curV + 6) * age + Math.sin(birth * 13) * 1.5 * age;
    const y = m.y - m.dvy * age + o.cur[1] * (o.curV + 6) * age;
    circle(c, x, y, 0.6 + (0.8 * age) / 1.3, WHITE, 0.75 * (1 - age / 1.3), lw(0.9, o.z));
  }
  if (m.state === 'armed' && m.kArm < 0.25) { const q = m.kArm / 0.25; circle(c, m.x, m.y, 5 + 3 * q, WHITE, 1 - q, lw(1.2, o.z)); }
}

/** The look of each id. The record is typed by the list, so a look the
 *  list gains without its card is a type error. */
export const SEA_MINE_LAY_LOOKS: Record<SeaMineLayStyle, SeaMineLayLook> = {
  'countdown-ring': countdownRing,
  'plain-drop': plainDrop,
  'stern-slide': sternSlide,
  'chute-flash': chuteFlash,
  'swing-cradle': swingCradle,
  'seed-line': seedLine,
  ripple,
  'safe-bubble': safeBubble,
};

/** The seconds the lay of a look draws, card 20's bubbles included. */
export function seaMineLayLife(style: SeaMineLayStyle): number {
  return Math.max(SEA_MINE_LAY_LOOKS[style].layLife ?? 0, 1.6);
}

/** Paints the lay of one sea mine in one look, with the bubbles of card 20. */
export function paintSeaMineLay(c: Ctx, style: SeaMineLayStyle, m: LaidMine, o: LayScene): void {
  SEA_MINE_LAY_LOOKS[style].lay?.(c, m, o);
  paintLayBubbles(c, m, o);
}

/** Paints one laid sea mine in one look, with the stream of card 20. */
export function paintLaidMine(c: Ctx, style: SeaMineLayStyle, m: LaidMine, o: LayScene): void {
  const look = SEA_MINE_LAY_LOOKS[style];
  if (look.mine) look.mine(c, m, o); else defaultLaidMine(c, m, o);
  paintMineBubbles(c, m, o);
}

/** Paints the loud tell of one look, with the clock of card 18. */
export function paintLayLoud(c: Ctx, style: SeaMineLayStyle, H: LayHull, o: LayScene): void {
  SEA_MINE_LAY_LOOKS[style].loud?.(c, H, o);
  paintLoudClock(c, H, o);
}

// =================================================================================
// Round 072: a Sapper lays a deployable
// =================================================================================

/** The shape of a deployable kind on the map (§11.3). */
export type DeployShape = 'line' | 'disc' | 'point';

/** The shape of each kind. */
export function shapeOf(kind: DeployableKind): DeployShape {
  return kind === 'seine' || kind === 'palisade' ? 'line' : kind === 'cheval' ? 'disc' : 'point';
}

/** Everything a deployable lay look reads. The deployable lies at the
 *  origin, and the caller turns the context so a line lies along y. */
export interface DeployLay {
  kind: DeployableKind;
  /** The length of a line kind and the radius of the Cheval, map px. */
  L: number;
  Rc: number;
  /** True for the Palisade, the heavy kind. */
  heavy: boolean;
  /** The clock of the look, the drop, the anchor hold (live), the anchor
   *  time, and the share of the anchor time run (0 to 1). */
  t: number; tL: number; tS: number; dur: number; u: number;
  /** The seconds since the deployable went live; negative before. */
  k: number;
  laid: boolean; live: boolean; loud: boolean;
  /** The seconds of the loud lay, in the look's own seconds. */
  loudSec: number;
  /** The ink of the layer's side. */
  col: number;
  own: boolean;
  /** The Sapper now, in the frame of the deployable. */
  S: { x: number; y: number; hdg: number };
  /** The listener of a hostile view (its hold), or null. */
  listener: Pt | null;
  /** The ink of the viewer's own side, for the hostile view's bearing. */
  viewerCol: number;
  z: number;
  face: string;
  /** The mark of the `deployable` field, which stands in place of the
   *  placeholder (R2657): it paints the whole kind at the origin of the
   *  frame, a line along +x, at alpha `a`. The helpers turn, scale and clip
   *  it to the extent the card unrolls. */
  mark?: (c: Ctx, ink: number, a: number) => void;
}

/** One deployable lay look. */
export interface DeployableLayLook {
  under?(c: Ctx, o: DeployLay): void;
  draw(c: Ctx, o: DeployLay): void;
}

// ---- the placeholder deployable of rounds 070 to 074 -------------------------

function zz(q: number): number { const f = ((q % 2) + 2) % 2; return f < 1 ? -1 + 2 * f : 3 - 2 * f; }
/** 28 teeth round the Cheval. */
const ZSTEP = TAU / 56;
/** The saw edge of the Cheval from `a0` to `a1`. */
function zigzag(c: Ctx, R: number, a0: number, a1: number, amp: number, cont = false): void {
  zigPoint(c, R, a0, amp, cont);
  for (let k = Math.floor(a0 / ZSTEP) + 1; k * ZSTEP < a1 - 1e-6; k++) zigPoint(c, R, k * ZSTEP, amp, true);
  zigPoint(c, R, a1, amp, true);
}

/** One point of the saw edge at the angle `ang`: a move, or a line on. */
function zigPoint(c: Ctx, R: number, ang: number, amp: number, on: boolean): void {
  const r = R + amp * zz(ang / ZSTEP), x = Math.cos(ang) * r, y = Math.sin(ang) * r;
  if (on) c.lineTo(x, y); else c.moveTo(x, y);
}

/** A line kind from `y0` to `y1` along y: the Palisade a solid bar, the
 *  Seine a dashed rope with a float each 10 px. */
export function lineDep(c: Ctx, o: DeployLay, ink: number, a: number, y0: number, y1: number): void {
  if (!(a > 0) || !(y1 > y0)) return;
  if (o.mark) {
    const W = o.L + 20;
    c.save(); c.beginPath(); c.rect(-W, y0, 2 * W, y1 - y0); c.clip();
    c.rotate(Math.PI / 2); o.mark(c, ink, a); c.restore();
    return;
  }
  c.beginPath(); c.moveTo(0, y0); c.lineTo(0, y1); c.lineCap = 'butt';
  if (o.kind === 'palisade') { c.lineWidth = 3; c.strokeStyle = rgba(ink, a); c.stroke(); c.lineCap = 'round'; return; }
  c.lineWidth = lw(1.1, o.z); c.strokeStyle = rgba(ink, 0.9 * a); c.setLineDash([3, 2]); c.lineDashOffset = y0 + o.L / 2;
  c.stroke(); c.setLineDash([]); c.lineDashOffset = 0; c.lineCap = 'round';
  for (let f = -o.L / 2; f <= o.L / 2; f += 10) if (f >= y0 - 0.01 && f <= y1 + 0.01) disc(c, 0, f, 1.7, ink, a);
}

/** The Cheval: a saw-edged disc with a low fill. */
export function chevalDep(c: Ctx, o: DeployLay, ink: number, a: number, R = o.Rc, fillA = 0.12): void {
  if (!(R > 0.5) || !(a > 0)) return;
  if (o.mark) { const k = R / o.Rc; c.save(); c.scale(k, k); o.mark(c, ink, a); c.restore(); return; }
  c.beginPath(); zigzag(c, R, 0, TAU, 1.8 * Math.min(1, R / o.Rc)); c.closePath();
  Fl(c, ink, fillA * a); S(c, ink, 0.9 * a, lw(1.1, o.z));
}

/** A point kind at scale `s`: the picket buoy a ring with a dot, the lane
 *  light a diamond, the decoy buoy a hollow triangle. */
export function pointDep(c: Ctx, o: DeployLay, ink: number, a: number, s = 1): void {
  if (!(a > 0) || !(s > 0.02)) return;
  if (o.mark) { c.save(); c.scale(s, s); o.mark(c, ink, a); c.restore(); return; }
  if (o.kind === 'picket') { circle(c, 0, 0, 4 * s, ink, a, lw(1.2, o.z)); disc(c, 0, 0, 1.3 * s, ink, a); }
  else if (o.kind === 'lane') { path4(c, 0, -3 * s, 3 * s, 0, 0, 3 * s, -3 * s, 0); Fl(c, ink, a); }
  else { path3(c, 0, -3.46 * s, 3 * s, 1.73 * s, -3 * s, 1.73 * s); S(c, ink, a, lw(1.1, o.z)); }
}

/** The placeholder deployable whole, at alpha `a`. */
export function paintDeployPlaceholder(c: Ctx, o: DeployLay, ink: number, a: number): void {
  const sh = shapeOf(o.kind);
  if (sh === 'line') lineDep(c, o, ink, a, -o.L / 2, o.L / 2);
  else if (sh === 'disc') chevalDep(c, o, ink, a);
  else pointDep(c, o, ink, a, 1);
}

/** The half extents of a deployable. */
function extent(o: DeployLay): { hx: number; hy: number } {
  const sh = shapeOf(o.kind);
  return sh === 'line' ? { hx: 1.5, hy: o.L / 2 } : sh === 'disc' ? { hx: o.Rc + 2, hy: o.Rc + 2 } : { hx: 4.5, hy: 4.5 };
}

/** A stadium (a line with round ends) from the top centre, clockwise. */
function stadiumPath(c: Ctx, hx: number, hy: number): void {
  c.moveTo(0, -hy); c.arc(0, -hy + hx, hx, -Math.PI / 2, 0); c.lineTo(hx, hy - hx);
  c.arc(0, hy - hx, hx, 0, Math.PI); c.lineTo(-hx, -hy + hx); c.arc(0, -hy + hx, hx, Math.PI, Math.PI * 1.5);
}

/** The outline of a deployable grown by `g`, as an open path. */
function outline(c: Ctx, o: DeployLay, g: number): void {
  c.beginPath();
  const sh = shapeOf(o.kind);
  if (sh === 'line') stadiumPath(c, 2 + g, o.L / 2 + 2 + g);
  else c.arc(0, 0, (sh === 'disc' ? o.Rc + 2 : 5) + g, 0, TAU);
}

function flashOutline(c: Ctx, o: DeployLay, k: number, dur: number, g0: number, g1: number, ink: number = WHITE): void {
  if (!(k >= 0 && k < dur)) return;
  const q = k / dur;
  outline(c, o, lerp(g0, g1, outCubic(q))); S(c, ink, 0.8 * (1 - q), lw(1.2, o.z));
}

/** Four corner brackets round a box of half size hx by hy. */
function corners(c: Ctx, hx: number, hy: number, arm: number, ink: number, a: number, w: number): void {
  for (let i = 0; i < 4; i++) {
    const sx = CORNER_X[i], sy = CORNER_Y[i];
    path3(c, sx * hx, sy * (hy - arm), sx * hx, sy * hy, sx * (hx - arm), sy * hy, false); S(c, ink, a, w);
  }
}

/** A small chart anchor: the ring toward the deployable, the crown along
 *  `ang`. */
function anchorGlyph(c: Ctx, x: number, y: number, ang: number, ink: number, a: number, solid: boolean, z: number): void {
  if (!(a > 0)) return;
  c.save(); c.translate(x, y); c.rotate(ang);
  const w = lw(solid ? 1.3 : 0.9, z);
  circle(c, -3.4, 0, 1.1, ink, a, w); line(c, -2.3, 0, 3, 0, ink, a, w); line(c, -1.4, -2, -1.4, 2, ink, a, w);
  arc(c, 0.2, 0, 2.9, -1.2, 1.2, ink, a, w);
  if (solid) {
    disc(c, 0.2 + 2.9 * Math.cos(1.2), 2.9 * Math.sin(1.2), 0.9, ink, a);
    disc(c, 0.2 + 2.9 * Math.cos(1.2), -2.9 * Math.sin(1.2), 0.9, ink, a);
  }
  c.restore();
}

/** Card 05: where the stakes go (x, y, and the angle of a Cheval stake),
 *  how many there are, and how many blows each one takes. One set, kept
 *  from frame to frame, with the blows each stake took so far. */
interface StakeSet { n: number; blows: number; x: number[]; y: number[]; a: number[]; done: number[] }
const STAKES: StakeSet = { n: 0, blows: 0, x: [], y: [], a: [], done: [] };

function stake(sk: StakeSet, x: number, y: number, a = 0): void {
  sk.x[sk.n] = x; sk.y[sk.n] = y; sk.a[sk.n] = a; sk.n++;
}

function stakeSet(o: DeployLay): StakeSet {
  const h = o.L / 2, sk = STAKES;
  sk.n = 0;
  if (o.kind === 'seine') { stake(sk, 0, -h); stake(sk, 0, h); stake(sk, 0, 0); sk.blows = 3; }
  else if (o.kind === 'palisade') { stake(sk, 0, -h); stake(sk, 0, h); stake(sk, 0, -h / 3); stake(sk, 0, h / 3); sk.blows = 4; }
  else if (shapeOf(o.kind) === 'disc') {
    for (let i = 0; i < 6; i++) { const a = -Math.PI / 2 + (i * TAU) / 6; stake(sk, Math.cos(a) * o.Rc, Math.sin(a) * o.Rc, a); }
    sk.blows = 2;
  } else { stake(sk, 0, 0); sk.blows = 3; }
  return sk;
}

/** Card 07: the pack the crane carries, and its shadow. */
function pack(c: Ctx, o: DeployLay, x: number, y: number, r: number, a: number, shadow: boolean): void {
  c.save(); c.translate(x, y); c.rotate(r);
  const ink = shadow ? DEEP : o.col, A = shadow ? 0.6 : a, sh = shapeOf(o.kind);
  if (sh === 'line') { const L = o.heavy ? 14 : 10; line(c, -L / 2, 0, L / 2, 0, ink, A, o.heavy ? 3.4 : 2.4); }
  else if (sh === 'disc') {
    disc(c, 0, 0, 5, ink, shadow ? A : 0.35 * A);
    if (!shadow) { c.beginPath(); zigzag(c, 5, 0, TAU, 0.8); c.closePath(); S(c, ink, A, lw(1, o.z)); }
  } else if (shadow) disc(c, 0, 0, 4, DEEP, A);
  else pointDep(c, o, ink, A, 1);
  c.restore();
}

// ---- the eight looks ---------------------------------------------------------------

/** Card 01 Plain Set: the deployable shows dim with a white cross blinking
 *  on its anchor, then full, and one white ring leaves its outline. */
const plainSet: DeployableLayLook = {
  draw(c, o) {
    if (!o.laid) return;
    paintDeployPlaceholder(c, o, o.col, o.live ? 1 : 0.35);
    if (!o.live) {
      const a = 0.45 + 0.55 * Math.abs(Math.sin((o.t - o.tL) * 6));
      line(c, -3, 0, 3, 0, WHITE, a, lw(1.1, o.z)); line(c, 0, -3, 0, 3, WHITE, a, lw(1.1, o.z));
    } else flashOutline(c, o, o.k, 0.6, 2, 14);
  },
};

/** Card 02 Carpet Unroll: two white roll bars run out from the drop across
 *  the heading, and the deployable lies flat behind them. */
const carpetUnroll: DeployableLayLook = {
  draw(c, o) {
    if (!o.laid) return;
    const p = outCubic(clamp01(o.u / 0.7)), col = o.col, sh = shapeOf(o.kind);
    const capA = o.live ? Math.max(0, 1 - o.k / 0.5) : 1, capC = o.live || p < 1 ? WHITE : col;
    if (sh === 'line') {
      const e = (o.L / 2) * p, bw = o.heavy ? 3.6 : 2.4, bl = o.heavy ? 10 : 7;
      lineDep(c, o, col, 1, -e, e);
      if (capA > 0) for (const s of [-1, 1]) line(c, -bl / 2, s * e, bl / 2, s * e, capC, 0.95 * capA, lw(bw, o.z));
    } else if (sh === 'disc') {
      const e = (o.Rc + 3) * p;
      c.save(); c.beginPath(); c.rect(-o.Rc - 10, -e, 2 * o.Rc + 20, 2 * e); c.clip(); chevalDep(c, o, col, 1); c.restore();
      if (capA > 0) {
        for (const s of [-1, 1]) {
          const y = s * Math.min(e, o.Rc), hw = Math.max(4, Math.sqrt(Math.max(0, o.Rc * o.Rc - y * y)) + 2);
          line(c, -hw, y, hw, y, capC, 0.95 * capA, lw(2.4, o.z));
        }
      }
    } else {
      const e = 5 * p;
      c.save(); c.beginPath(); c.rect(-7, -e, 14, 2 * e); c.clip(); pointDep(c, o, col, 1, 1); c.restore();
      if (capA > 0) for (const s of [-1, 1]) line(c, -4.5, s * e, 4.5, s * e, capC, 0.95 * capA, lw(1.6, o.z));
    }
  },
};

/** Card 03 Reel Pays Out: a reel on the drop turns while the deployable
 *  pays out of it; a brake bar crosses it when the anchor holds. */
const reelPaysOut: DeployableLayLook = {
  draw(c, o) {
    if (!o.laid) return;
    const col = o.col, pay = clamp01(o.u / 0.75), R = o.heavy ? 6.5 : 4.5, sh = shapeOf(o.kind);
    const ang = (o.heavy ? 2.5 : 5) * TAU * outCubic(pay);
    if (sh === 'line') {
      const e = (o.L / 2) * pay;
      if (o.kind === 'seine') {
        for (const s of [-1, 1]) {
          c.beginPath(); c.moveTo(0, s * e); c.lineTo(0, 0); c.lineWidth = lw(1.1, o.z); c.strokeStyle = rgba(col, 0.9); c.lineCap = 'butt';
          c.setLineDash([3, 2]); c.stroke(); c.setLineDash([]); c.lineCap = 'round';
          for (let f = 0; f <= e + 0.01; f += 10) disc(c, 0, s * (e - f), 1.7, col, 1);
        }
      } else lineDep(c, o, col, 1, -e, e);
    } else if (sh === 'disc') {
      const x = smooth(0.75, 0.92, o.u), mx = 2.5 * TAU;
      if (x < 1 && pay > 0) {
        c.beginPath();
        const n = Math.max(2, Math.ceil(140 * pay));
        for (let i = 0; i <= n; i++) {
          const th = (mx * pay * i) / n, r = R + ((o.Rc - R) * th) / mx, X = Math.cos(th + Math.PI) * r, Y = Math.sin(th + Math.PI) * r;
          if (i) c.lineTo(X, Y); else c.moveTo(X, Y);
        }
        S(c, col, 0.9 * (1 - x), lw(1.1, o.z));
      }
      if (x > 0) chevalDep(c, o, col, x);
    } else {
      const e = 4.5 + 8 * pay, ra = o.live ? Math.max(0, 1 - o.k / 0.6) : 1;
      line(c, -4.5, 0, -e, 0, PALE, 0.8 * ra, lw(0.9, o.z));
      anchorGlyph(c, -e - 3.4, 0, Math.PI, pay >= 1 ? col : PALE, ra, pay >= 1, o.z);
      pointDep(c, o, col, 1, 1);
    }
    const rA = o.live ? Math.max(0, 1 - o.k / 0.3) : 1;
    if (rA > 0) {
      const rr = R * (o.live ? 1 + (0.5 * o.k) / 0.3 : 1);
      disc(c, 0, 0, rr, DEEP, 0.85 * rA); circle(c, 0, 0, rr, col, rA, lw(1.3, o.z));
      for (let i = 0; i < 3; i++) { const a = ang + (i * TAU) / 3; line(c, 0, 0, Math.cos(a) * rr * 0.8, Math.sin(a) * rr * 0.8, WHITE, 0.9 * rA, lw(1, o.z)); }
      if (pay >= 1 && !o.live) line(c, -rr - 2, 0, rr + 2, 0, col, 1, lw(1.8, o.z));
    }
    if (o.live && o.k < 0.4) circle(c, 0, 0, R + (9 * o.k) / 0.4, WHITE, 0.7 * (1 - o.k / 0.4), lw(1, o.z));
  },
};

/** Card 04 Fold Opens: the deployable lands folded and opens like a rule or
 *  a fan; a hinge pin locks when the shape is full. */
const foldOpens: DeployableLayLook = {
  draw(c, o) {
    if (!o.laid) return;
    const col = o.col, f = outCubic(clamp01(o.u / 0.75)), locked = o.u >= 0.75, sh = shapeOf(o.kind);
    const pinA = o.live ? Math.max(0, 1 - o.k / 0.5) : locked ? 1 : 0, pinC = o.live ? WHITE : col;
    if (sh === 'line') {
      if (f >= 1) lineDep(c, o, col, 1, -o.L / 2, o.L / 2);
      else {
        const N = o.L / 10, th = (1 - f) * 1.25, sy = 10 * Math.cos(th), sx = 10 * Math.sin(th), tot = N * sy;
        // The path of the leaves, then a float at each fold: the points are
        // computed twice and held in no array.
        c.beginPath();
        for (let i = 0; i <= N; i++) {
          const qx = i % 2 ? sx / 2 : -sx / 2, qy = -tot / 2 + i * sy;
          if (i) c.lineTo(qx, qy); else c.moveTo(qx, qy);
        }
        S(c, col, 0.9, o.heavy ? 3 : lw(1.1, o.z));
        for (let i = 0; i <= N; i++) {
          const qx = i % 2 ? sx / 2 : -sx / 2, qy = -tot / 2 + i * sy;
          if (o.kind === 'seine') disc(c, qx, qy, 1.7, col, 1); else disc(c, qx, qy, 1.1, PALE, 0.9);
        }
      }
      if (pinA > 0) for (let s = -1; s <= 1; s += 2) { c.beginPath(); c.rect(-2.5, (s * o.L) / 2 - 2.5, 5, 5); S(c, pinC, pinA, lw(1.2, o.z)); }
    } else if (sh === 'disc') {
      const L = 12, sp = TAU / L, ribA = o.live ? Math.max(0, 1 - o.k / 0.5) : 1;
      if (o.live) chevalDep(c, o, col, 1);
      else {
        for (let i = 0; i < L; i++) {
          const ca = Math.PI + (i - (L - 1) / 2) * sp * f, a0 = ca - sp / 2, a1 = ca + sp / 2;
          c.beginPath(); c.moveTo(0, 0); zigzag(c, o.Rc, a0, a1, 1.8, true); c.closePath(); Fl(c, col, 0.12);
          c.beginPath(); zigzag(c, o.Rc, a0, a1, 1.8); S(c, col, 0.9, lw(1.1, o.z));
        }
      }
      if (ribA > 0) {
        for (let i = 0; i < L; i++) {
          const ca = Math.PI + (i - (L - 1) / 2) * sp * f - sp / 2;
          line(c, 0, 0, Math.cos(ca) * (o.Rc - 1), Math.sin(ca) * (o.Rc - 1), PALE, 0.45 * ribA, lw(0.8, o.z));
        }
      }
      if (pinA > 0) disc(c, 0, 0, 2.6, pinC, pinA);
    } else {
      c.save(); c.scale(Math.max(0.06, f), 1); pointDep(c, o, col, 1, 1); c.restore();
      if (f < 1) line(c, 0, -5.5, 0, 5.5, PALE, 0.6 * (1 - f), lw(0.9, o.z));
      if (pinA > 0) line(c, -3, 7, 3, 7, pinC, pinA, lw(1.4, o.z));
    }
  },
};

/** Card 05 Stake Driven: stakes go into the floor one blow at a time; the
 *  last blow sets the anchor. */
const stakeDriven: DeployableLayLook = {
  draw(c, o) {
    if (!o.laid) return;
    const col = o.col, sk = stakeSet(o), n = sk.n, B = n * sk.blows, d = (o.dur * 0.85) / B, el = o.t - o.tL;
    const sh = shapeOf(o.kind);
    // The blows each stake took; a stake is driven when it took them all.
    const done = sk.done;
    for (let i = 0; i < n; i++) {
      let m = 0;
      for (let b = 0; b < sk.blows; b++) if (el >= (i * sk.blows + b + 0.8) * d) m++;
      done[i] = m;
    }
    if (sh === 'line') {
      const h = o.L / 2;
      if (done[0] === sk.blows && done[1] === sk.blows) lineDep(c, o, col, 1, -h, h);
      else {
        const pp = (done[0] + done[1]) / (2 * sk.blows), b = 12 * (1 - pp);
        c.beginPath(); c.moveTo(0, -h); c.quadraticCurveTo(-2 * b, 0, 0, h);
        if (o.kind === 'palisade') S(c, col, 0.4, 3); else S(c, col, 0.45, lw(1.1, o.z), [3, 2]);
      }
    } else if (sh === 'disc') {
      let nd = 0;
      for (let i = 0; i < n; i++) if (done[i] === sk.blows) nd++;
      if (nd === n) chevalDep(c, o, col, 1);
      else {
        disc(c, 0, 0, o.Rc, col, (0.07 * nd) / n);
        for (let i = 0; i < n; i++) {
          const j = (i + 1) % n;
          if (done[i] === sk.blows && done[j] === sk.blows) {
            const a0 = sk.a[i];
            c.beginPath(); zigzag(c, o.Rc, a0, a0 + TAU / n, 1.8); S(c, col, 0.9, lw(1.1, o.z));
          }
        }
      }
    } else pointDep(c, o, col, 0.3 + (0.7 * done[0]) / sk.blows, 1);
    const j = Math.floor(el / d), ci = Math.floor(j / sk.blows);
    if (j >= 0 && j < B) {
      const qx = sk.x[ci], qy = sk.y[ci], q = (el - j * d) / (0.8 * d), R0 = o.heavy ? 10 : 7;
      if (q < 1) circle(c, qx, qy, 1 + (R0 - 1) * (1 - q), WHITE, 0.35 + 0.6 * q, lw(o.heavy ? 1.6 : 1.2, o.z));
      else disc(c, qx, qy, o.heavy ? 2.8 : 2.2, WHITE, 0.95);
    }
    const sa = o.live ? Math.max(0, 1 - o.k / 0.6) : 1, ss = o.heavy ? 2.8 : 2.2;
    if (sa > 0) {
      for (let i = 0; i < n; i++) {
        if (!done[i] && i !== ci) continue;
        c.beginPath(); c.rect(sk.x[i] - ss / 2, sk.y[i] - ss / 2, ss, ss);
        Fl(c, done[i] === sk.blows ? col : PALE, sa * (0.4 + (0.6 * done[i]) / sk.blows));
      }
    }
    if (o.live) flashOutline(c, o, o.k, 0.5, 2, 12, o.col);
  },
};

/** Card 06 Blueprint Print: the own view shows the plan ahead of the drop;
 *  at the drop a print head scans it and the deployable fills in behind. */
const blueprintPrint: DeployableLayLook = {
  draw(c, o) {
    const f = extent(o), pre = o.tL - 1.4;
    const ga = o.own ? smooth(pre, pre + 0.3, o.t) * (o.live ? Math.max(0, 1 - o.k / 0.4) : 1) : 0;
    if (ga > 0) {
      outline(c, o, 3); S(c, o.live ? WHITE : PALE, (o.live ? 0.9 : 0.65) * ga, lw(1, o.z), [px(3, o.z), px(3, o.z)]);
      if (!o.laid) { line(c, -6, 0, 6, 0, PALE, 0.75 * ga, lw(0.9, o.z)); line(c, 0, -6, 0, 6, PALE, 0.75 * ga, lw(0.9, o.z)); }
    }
    if (!o.laid) return;
    const p = clamp01(o.u / 0.85), top = -f.hy - 4, bot = f.hy + 4, y = lerp(top, bot, p);
    c.save(); c.beginPath(); c.rect(-f.hx - 8, top - 4, 2 * f.hx + 16, y - top + 4); c.clip();
    paintDeployPlaceholder(c, o, o.col, 1); c.restore();
    if (p < 1) {
      line(c, -f.hx - 6, y, f.hx + 6, y, WHITE, 0.9, lw(1.3, o.z));
      disc(c, -f.hx - 6, y, 1.4, WHITE, 0.9); disc(c, f.hx + 6, y, 1.4, WHITE, 0.9);
    }
  },
};

/** Card 07 Crane Lowers: the own Sapper carries the deployable packed small
 *  and lowers it on a cable; at the floor the pack opens. */
const craneLowers: DeployableLayLook = {
  draw(c, o) {
    const col = o.col, sp = o.S, sh = shapeOf(o.kind);
    if (!o.laid) {
      if (o.own) {
        c.save(); c.translate(sp.x, sp.y); line(c, 0, 1.5, 0, 5, PALE, 0.7, lw(0.8, o.z)); pack(c, o, 0, 6, 0, 1, false); c.restore();
      }
      return;
    }
    const lowU = 0.6, d = 1 - outCubic(clamp01(o.u / lowU)), by = 6 * d;
    if (o.u < lowU) {
      line(c, sp.x - 6, sp.y, 0, by, PALE, 0.75, lw(0.9, o.z));
      pack(c, o, 7 * d, by + 8 * d, ((1 - d) * Math.PI) / 2, 1, true);
      c.save(); c.translate(0, by); c.scale(1 + 0.3 * d, 1 + 0.3 * d); pack(c, o, 0, 0, ((1 - d) * Math.PI) / 2, 1, false); c.restore();
      return;
    }
    const r = clamp01((o.u - lowU) / 0.28), q = outCubic(clamp01((o.u - lowU) / 0.25)), kd = o.t - (o.tL + lowU * o.dur);
    if (r < 1) line(c, sp.x - 6, sp.y, lerp(0, sp.x - 6, r), lerp(0, sp.y, r), PALE, 0.65 * (1 - r), lw(0.9, o.z), [px(2, o.z), px(2, o.z)]);
    if (kd < 0.5) { const qq = kd / 0.5; outline(c, o, 1 + 10 * outCubic(qq)); S(c, PALE, 0.6 * (1 - qq), lw(1.1, o.z)); }
    if (sh === 'line') { const e = lerp(o.heavy ? 7 : 5, o.L / 2, q); lineDep(c, o, col, o.live ? 1 : 0.8, -e, e); }
    else if (sh === 'disc') chevalDep(c, o, col, o.live ? 1 : 0.8, lerp(5, o.Rc, q));
    else pointDep(c, o, col, o.live ? 1 : 0.8, 1);
    if (o.live) flashOutline(c, o, o.k, 0.4, 1, 5);
  },
};

/** Card 11 Splash Rings: the water does the work; ripples run out along a
 *  line, one ring fills the Cheval, two ripples carry a point up. */
const splashRings: DeployableLayLook = {
  draw(c, o) {
    if (!o.laid) return;
    const col = o.col, el = o.t - o.tL, sh = shapeOf(o.kind);
    if (sh === 'line') {
      const h = o.L / 2, nK = Math.round(h / 10), life = o.heavy ? 1.0 : 0.6, gap = (o.dur * 0.7) / (nK + 1), Rr = o.heavy ? 12 : 8;
      let e = -1;
      for (let k = 0; k <= nK; k++) {
        const ts = k * gap;
        if (el >= ts) e = k * 10;
        const q = (el - ts) / life;
        if (q >= 0 && q < 1) {
          for (let j = 0; j < 2; j++) {
            if (k === 0 && j) continue;
            const y = (j ? 1 : -1) * k * 10;
            circle(c, 0, y, 1 + Rr * outCubic(q), PALE, 0.6 * (1 - q), lw(o.heavy ? 1.4 : 1, o.z));
            if (o.heavy && q > 0.3) circle(c, 0, y, 1 + Rr * 0.7 * outCubic((q - 0.3) / 0.7), PALE, 0.45 * (1 - q), lw(1, o.z));
          }
        }
      }
      if (e > 0) lineDep(c, o, col, 1, -e, e);
      else if (e === 0 && o.kind === 'seine') disc(c, 0, 0, 1.7, col, 1);
    } else if (sh === 'disc') {
      const q = clamp01(el / (o.dur * 0.55)), r = o.Rc * outCubic(q), za = smooth(0.55, 0.8, o.u);
      if (za <= 0) { disc(c, 0, 0, r, col, 0.12); circle(c, 0, 0, r, WHITE, 0.75, lw(1.4, o.z)); }
      else {
        c.beginPath(); zigzag(c, o.Rc, 0, TAU, 1.8 * za); c.closePath(); Fl(c, col, 0.12); S(c, col, 0.9, lw(1.1, o.z));
        if (za < 1) { c.beginPath(); zigzag(c, o.Rc, 0, TAU, 1.8 * za); c.closePath(); S(c, WHITE, 0.75 * (1 - za), lw(1.4, o.z)); }
      }
      const q2 = (el - o.dur * 0.55) / 0.6;
      if (q2 >= 0 && q2 < 1) circle(c, 0, 0, o.Rc + 18 * outCubic(q2), PALE, 0.5 * (1 - q2), lw(1, o.z));
    } else {
      for (const d0 of [0, 0.25]) {
        const q = (el - d0) / 0.7;
        if (q >= 0 && q < 1) circle(c, 0, 0, 1 + 11 * outCubic(q), PALE, 0.65 * (1 - q), lw(1, o.z));
      }
      pointDep(c, o, col, smooth(0.1, 0.4, el), 1);
    }
    if (o.live && o.k < 0.35) {
      const a = 1 - o.k / 0.35;
      outline(c, o, 3); S(c, WHITE, 0.8 * a, lw(1, o.z)); outline(c, o, 6); S(c, WHITE, 0.5 * a, lw(1, o.z));
    }
  },
};

/** Card 20 Loud Lay, under the Sapper, which joins every deployable lay
 *  look: a dashed ring at 1.5 × r19 drains over the loud lay, and sound
 *  rings leave the hull. */
export function paintLoudLayRing(c: Ctx, o: DeployLay): void {
  const el = o.t - o.tL, H = o.S;
  if (el < 0 || el >= o.loudSec) return;
  arc(c, H.x, H.y, 28.5, -Math.PI / 2, -Math.PI / 2 + TAU * (1 - el / o.loudSec), PALE, 0.7, lw(1.3, o.z), [px(3, o.z), px(2, o.z)]);
  for (let j = 0; j < 4; j++) {
    const q = (el - j * 0.75) / 1.0;
    if (q >= 0 && q < 1) circle(c, H.x, H.y, 29 + 30 * q, PALE, 0.32 * (1 - q), lw(1, o.z));
  }
}

/** The bearing of card 20 Loud Lay: a dashed line in the viewer's ink from
 *  its listener `from` to the loud Sapper `to`. The map draws it for a loud
 *  Sapper contact of another faction (R2841). */
export function paintLoudBearing(c: Ctx, from: Pt, to: Pt, ink: number, z: number): void {
  line(c, from[0], from[1], to[0], to[1], ink, 0.35, lw(1, z), [px(4, z), px(3, z)]);
}

/** Card 20 Loud Lay in a hostile view: a bearing from the listener to the
 *  loud Sapper, and a HEARD frame on the deployable when it is live. */
export function paintLoudLayHeard(c: Ctx, o: DeployLay): void {
  if (o.own || !o.listener || !o.laid) return;
  const H = o.listener, sp = o.S;
  if (o.loud) paintLoudBearing(c, H, [sp.x, sp.y], o.viewerCol, o.z);
  if (!o.live) return;
  const f = extent(o), q = outCubic(clamp01(o.k / 0.4)), g = 4 + 10 * (1 - q);
  const hx = f.hx + g + (shapeOf(o.kind) === 'line' ? 3 : 0), hy = f.hy + g;
  line(c, H[0], H[1], 0, 0, o.viewerCol, 0.4, lw(1, o.z), [px(2, o.z), px(3, o.z)]);
  corners(c, hx, hy, Math.min(6, Math.min(hx, hy) * 0.6), WHITE, 0.85, lw(1.2, o.z));
  text(c, WORDS.heard, 0, -hy - 6 / o.z, `500 ${8 / o.z}px ${o.face}`, WHITE, 0.85, 'center');
}

/** The look of each id. The record is typed by the list, so a look the
 *  list gains without its card is a type error. */
export const DEPLOYABLE_LAY_LOOKS: Record<DeployableLayStyle, DeployableLayLook> = {
  'carpet-unroll': carpetUnroll,
  'plain-set': plainSet,
  'reel-pays-out': reelPaysOut,
  'fold-opens': foldOpens,
  'stake-driven': stakeDriven,
  'blueprint-print': blueprintPrint,
  'crane-lowers': craneLowers,
  'splash-rings': splashRings,
};

/** Paints the lay of one deployable in one look, with card 20 Loud Lay:
 *  the ring and the sound rings under, the look, and the hostile bearing
 *  and frame over. */
export function paintDeployableLay(c: Ctx, style: DeployableLayStyle, o: DeployLay): void {
  const look = DEPLOYABLE_LAY_LOOKS[style];
  paintLoudLayRing(c, o);
  look.under?.(c, o);
  look.draw(c, o);
  paintLoudLayHeard(c, o);
}
